/**
 * QRGenerator — QR-codes maken met stijlopties (dot-vormen, kleuren, logo) en
 * export naar PNG én SVG. Full-width, gedeelde PageHeader. Bouwt op qr-code-styling.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, Tooltip, IconButton } from '@mui/material';
import QRCodeStyling from 'qr-code-styling';
import toast from 'react-hot-toast';
import PageHeader from '../../ui-components/PageHeader';

const T = {
  primary: '#526075', tertiary: '#635983', tertiaryContainer: '#dbcdff',
  bg: '#f8f9fa', surface: '#ffffff', surfaceLow: '#f1f4f5', surfaceHigh: '#e5e9eb',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  shadow: '0 10px 30px rgba(45,51,53,0.06)',
  font: '"Manrope", system-ui, sans-serif', fontBody: '"Inter", system-ui, sans-serif',
  fontMono: '"JetBrains Mono", "Menlo", monospace',
};

const Icon = ({ name, size = 20, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'wght' 500", lineHeight: 1, ...sx }}>{name}</span>
);

const DOT_TYPES = [
  ['square', 'Vierkant'], ['rounded', 'Afgerond'], ['dots', 'Stippen'],
  ['classy', 'Chic'], ['extra-rounded', 'Extra rond'],
];
const CORNER_SQ = [['square', 'Vierkant'], ['dot', 'Stip'], ['extra-rounded', 'Rond']];
const CORNER_DOT = [['square', 'Vierkant'], ['dot', 'Stip']];
const ECC = [['L', 'L · 7%'], ['M', 'M · 15%'], ['Q', 'Q · 25%'], ['H', 'H · 30%']];

function Section({ label, children }) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.6rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: T.outline, mb: 1 }}>{label}</Typography>
      {children}
    </Box>
  );
}

function Segmented({ options, value, onChange }) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      {options.map(([val, lbl]) => {
        const active = value === val;
        return (
          <Box key={val} onClick={() => onChange(val)} sx={{
            px: 1.25, py: 0.6, borderRadius: '0.5rem', cursor: 'pointer',
            fontFamily: T.fontBody, fontSize: '0.76rem', fontWeight: 600,
            bgcolor: active ? T.tertiary : T.surfaceLow, color: active ? '#fff' : T.secondary,
            border: `1px solid ${active ? T.tertiary : T.surfaceHigh}`, transition: 'all 0.12s',
            '&:hover': active ? {} : { bgcolor: T.surfaceHigh },
          }}>{lbl}</Box>
        );
      })}
    </Box>
  );
}

function ColorField({ label, value, onChange }) {
  return (
    <Box sx={{ flex: 1 }}>
      <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', color: T.outline, mb: 0.5 }}>{label}</Typography>
      <Box component="label" sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: T.surfaceLow, borderRadius: '0.5rem', px: 1, py: 0.75, cursor: 'pointer' }}>
        <Box sx={{ width: 24, height: 24, borderRadius: '0.35rem', bgcolor: value, border: `1px solid ${T.surfaceHigh}`, flexShrink: 0 }} />
        <Typography sx={{ fontFamily: T.fontMono, fontSize: '0.78rem', color: T.text }}>{value.toUpperCase()}</Typography>
        <Box component="input" type="color" value={value} onChange={(e) => onChange(e.target.value)} sx={{ width: 0, height: 0, opacity: 0, position: 'absolute' }} />
      </Box>
    </Box>
  );
}

function Slider({ label, value, min, max, step = 1, suffix = '', onChange }) {
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', color: T.outline }}>{label}</Typography>
        <Typography sx={{ fontFamily: T.fontMono, fontSize: '0.7rem', color: T.text }}>{value}{suffix}</Typography>
      </Box>
      <Box component="input" type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        sx={{ width: '100%', accentColor: T.tertiary }} />
    </Box>
  );
}

export default function QRGenerator() {
  const [data, setData] = useState('https://intern.fullframe.nl');
  const [fg, setFg] = useState('#2d3335');
  const [bg, setBg] = useState('#ffffff');
  const [dotType, setDotType] = useState('rounded');
  const [cornerSq, setCornerSq] = useState('extra-rounded');
  const [cornerDot, setCornerDot] = useState('dot');
  const [ecc, setEcc] = useState('Q');
  const [size, setSize] = useState(320);
  const [margin, setMargin] = useState(12);
  const [logo, setLogo] = useState('');

  const containerRef = useRef(null);
  const qrRef = useRef(null);
  const fileRef = useRef(null);

  const opts = useMemo(() => ({
    width: 300, height: 300, type: 'svg',
    data: data || ' ',
    margin,
    qrOptions: { errorCorrectionLevel: ecc },
    dotsOptions: { color: fg, type: dotType },
    backgroundOptions: { color: bg },
    cornersSquareOptions: { color: fg, type: cornerSq },
    cornersDotOptions: { color: fg, type: cornerDot },
    image: logo || undefined,
    imageOptions: { crossOrigin: 'anonymous', margin: 6, imageSize: 0.4 },
  }), [data, fg, bg, dotType, cornerSq, cornerDot, ecc, margin, logo]);

  useEffect(() => {
    qrRef.current = new QRCodeStyling(opts);
    if (containerRef.current) {
      containerRef.current.innerHTML = '';
      qrRef.current.append(containerRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { qrRef.current?.update(opts); }, [opts]);

  const download = (ext) => {
    if (!data.trim()) { toast.error('Vul eerst inhoud in'); return; }
    qrRef.current?.update({ ...opts, width: size, height: size });
    setTimeout(() => {
      qrRef.current?.download({ name: 'qr-code', extension: ext });
      qrRef.current?.update(opts);
    }, 60);
  };

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setLogo(reader.result);
    reader.readAsDataURL(file);
  };

  const DownloadBtn = ({ ext, icon }) => (
    <Box onClick={() => download(ext)} sx={{
      flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 0.75,
      px: 2, py: 1.1, borderRadius: '0.6rem', cursor: 'pointer',
      background: `linear-gradient(135deg, ${T.tertiary}, ${T.primary})`, color: '#fff',
      fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.85rem',
      boxShadow: '0 4px 12px rgba(99,89,131,0.22)', transition: 'transform 0.15s',
      '&:hover': { transform: 'translateY(-1px)' },
    }}>
      <Icon name={icon} size={17} /> {ext.toUpperCase()}
    </Box>
  );

  return (
    <Box sx={{ width: '100%', bgcolor: T.bg, minHeight: 'calc(100vh - 44px)' }}>
      <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 8 }}>

        <PageHeader
          icon="qr_code_2"
          title="QR-generator"
          subtitle="Maak QR-codes met stijlopties en logo — export naar PNG en SVG."
          accent={{ color: T.tertiary, bg: T.tertiaryContainer }}
        />

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 360px' }, gap: 3, alignItems: 'start' }}>

          {/* ── Opties ── */}
          <Box sx={{ bgcolor: T.surface, borderRadius: '0.9rem', p: 3, border: `1px solid ${T.surfaceHigh}`, boxShadow: T.shadow }}>
            <Section label="Inhoud (URL of tekst)">
              <Box component="textarea" value={data} onChange={(e) => setData(e.target.value)} rows={2}
                sx={{ width: '100%', resize: 'vertical', bgcolor: T.surfaceLow, border: 'none', borderRadius: '0.5rem', p: 1.5, color: T.text, outline: 'none', fontFamily: T.fontBody, fontSize: '0.9rem', '&:focus': { boxShadow: `0 0 0 2px ${T.tertiary}40` } }} />
            </Section>

            <Section label="Kleuren">
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <ColorField label="Voorgrond" value={fg} onChange={setFg} />
                <ColorField label="Achtergrond" value={bg} onChange={setBg} />
              </Box>
            </Section>

            <Section label="Stijl — patroon"><Segmented options={DOT_TYPES} value={dotType} onChange={setDotType} /></Section>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Section label="Hoek — vierkant"><Segmented options={CORNER_SQ} value={cornerSq} onChange={setCornerSq} /></Section>
              <Section label="Hoek — punt"><Segmented options={CORNER_DOT} value={cornerDot} onChange={setCornerDot} /></Section>
            </Box>

            <Section label="Logo (optioneel)">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box onClick={() => fileRef.current?.click()} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.85, borderRadius: '0.5rem', cursor: 'pointer', border: `1px solid ${T.surfaceHigh}`, bgcolor: T.surfaceLow, fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: 600, color: T.secondary, '&:hover': { bgcolor: T.surfaceHigh } }}>
                  <Icon name="image" size={16} /> {logo ? 'Vervangen' : 'Uploaden'}
                </Box>
                {logo && (
                  <>
                    <Box component="img" src={logo} alt="" sx={{ width: 34, height: 34, borderRadius: '0.4rem', objectFit: 'cover', border: `1px solid ${T.surfaceHigh}` }} />
                    <Tooltip title="Logo verwijderen" arrow><IconButton size="small" onClick={() => setLogo('')} sx={{ color: T.outline }}><Icon name="close" size={16} /></IconButton></Tooltip>
                  </>
                )}
                <input ref={fileRef} type="file" accept="image/*" onChange={onLogo} style={{ display: 'none' }} />
              </Box>
            </Section>

            <Section label="Foutcorrectie">
              <Segmented options={ECC} value={ecc} onChange={setEcc} />
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outline, mt: 1, lineHeight: 1.5 }}>
                Bepaalt hoeveel van de code beschadigd of bedekt mag zijn en tóch nog scanbaar blijft:
                L ≈ 7%, M ≈ 15%, Q ≈ 25%, H ≈ 30%. Hoger = robuuster (en handig als je een logo in het
                midden plaatst), maar de code wordt wel dichter/complexer. Voor een logo raden we minimaal <strong>Q</strong> aan.
              </Typography>
            </Section>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Slider label="Exportgrootte" value={size} min={128} max={1024} step={16} suffix="px" onChange={setSize} />
              <Slider label="Marge" value={margin} min={0} max={40} suffix="px" onChange={setMargin} />
            </Box>
          </Box>

          {/* ── Preview + download ── */}
          <Box sx={{ position: { md: 'sticky' }, top: 20, bgcolor: T.surface, borderRadius: '0.9rem', p: 3, border: `1px solid ${T.surfaceHigh}`, boxShadow: T.shadow, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
            <Box ref={containerRef} sx={{ '& svg': { display: 'block', width: '100%', height: 'auto', borderRadius: '0.6rem' }, width: '100%', maxWidth: 300 }} />
            <Box sx={{ display: 'flex', gap: 1.5, width: '100%' }}>
              <DownloadBtn ext="png" icon="image" />
              <DownloadBtn ext="svg" icon="polyline" />
            </Box>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', color: T.outline, textAlign: 'center' }}>
              PNG = pixels (export op {size}px) · SVG = schaalbaar vector
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
